var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged, sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { auth } from '../config/firebase';
import { sendEmailVerification, reload } from 'firebase/auth';
import apiClient, { setupAuthInterceptors } from '../services/apiClient';
import { getFriendlyErrorMessage } from '../lib/errorHandler';
// Safe environment accessor (avoids ReferenceError when process is not defined in browser runtime)
var envVar = function (key) {
    try {
        if (typeof process !== 'undefined' && process.env && process.env[key]) {
            return process.env[key];
        }
    }
    catch (_a) {
        /* no-op */
    }
    // Support optional window injected vars (e.g., via a runtime config script)
    if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
        return window._env_[key];
    }
    return undefined;
};
var AuthContext = createContext({});
export var useAuth = function () {
    var context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export var AuthProvider = function (_a) {
    var children = _a.children;
    var _b = useState(null), currentUser = _b[0], setCurrentUser = _b[1];
    var _c = useState(null), userProfile = _c[0], setUserProfile = _c[1];
    var _d = useState(true), loading = _d[0], setLoading = _d[1];
    var _e = useState(false), emailVerified = _e[0], setEmailVerified = _e[1];
    // Debug logging to track AuthProvider renders
    console.log('[AuthProvider] Render', {
        hasCurrentUser: !!currentUser,
        loading: loading,
        emailVerified: emailVerified,
        hasUserProfile: !!userProfile
    });
    // Initialize Google Auth Provider
    var googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({
        prompt: 'select_account'
    });
    var signup = function (email, password, displayName, firstName, lastName) { return __awaiter(void 0, void 0, void 0, function () {
        var userCredential, _a, actionCodeSettings, e_1, userData, maxAttempts, _loop_1, attempt, state_1, error_1, friendlyMessage;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 15, , 16]);
                    return [4 /*yield*/, createUserWithEmailAndPassword(auth, email, password)];
                case 1:
                    userCredential = _c.sent();
                    if (!(displayName && userCredential.user)) return [3 /*break*/, 3];
                    return [4 /*yield*/, updateProfile(userCredential.user, { displayName: displayName })];
                case 2:
                    _c.sent();
                    _c.label = 3;
                case 3:
                    _c.trys.push([3, 5, , 6]);
                    return [4 /*yield*/, reload(userCredential.user)];
                case 4:
                    _c.sent();
                    return [3 /*break*/, 6];
                case 5:
                    _a = _c.sent();
                    return [3 /*break*/, 6];
                case 6:
                    if (!(userCredential.user && !userCredential.user.emailVerified)) return [3 /*break*/, 10];
                    actionCodeSettings = __assign({ url: (envVar('REACT_APP_ACTION_URL') || "".concat(window.location.origin, "/auth/action")), handleCodeInApp: true }, (envVar('REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN') ? { dynamicLinkDomain: envVar('REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN') } : {}));
                    _c.label = 7;
                case 7:
                    _c.trys.push([7, 9, , 10]);
                    return [4 /*yield*/, sendEmailVerification(userCredential.user, actionCodeSettings)];
                case 8:
                    _c.sent();
                    console.info('[Auth] Verification email dispatched');
                    return [3 /*break*/, 10];
                case 9:
                    e_1 = _c.sent();
                    console.error('[Auth] Failed to send verification email:', e_1 === null || e_1 === void 0 ? void 0 : e_1.code, (e_1 === null || e_1 === void 0 ? void 0 : e_1.message) || e_1);
                    return [3 /*break*/, 10];
                case 10:
                    if (!userCredential.user) return [3 /*break*/, 14];
                    userData = { first_name: firstName, last_name: lastName };
                    maxAttempts = 4;
                    _loop_1 = function (attempt) {
                        var token, error_2, status_1, backoff_1;
                        return __generator(this, function (_d) {
                            switch (_d.label) {
                                case 0:
                                    _d.trys.push([0, 3, , 9]);
                                    return [4 /*yield*/, userCredential.user.getIdToken(true)];
                                case 1:
                                    token = _d.sent();
                                    return [4 /*yield*/, apiClient.put('/api/auth/profile', userData, {
                                            headers: {
                                                'Authorization': "Bearer ".concat(token),
                                                'Content-Type': 'application/json'
                                            }
                                        })];
                                case 2:
                                    _d.sent();
                                    if (attempt > 1)
                                        console.info("[Auth] Profile sync succeeded on retry attempt ".concat(attempt));
                                    return [2 /*return*/, "break"];
                                case 3:
                                    error_2 = _d.sent();
                                    status_1 = (_b = error_2 === null || error_2 === void 0 ? void 0 : error_2.response) === null || _b === void 0 ? void 0 : _b.status;
                                    if (!(status_1 === 401)) return [3 /*break*/, 7];
                                    if (!(attempt === maxAttempts)) return [3 /*break*/, 4];
                                    console.warn('[Auth] Profile sync still 401 after retries. Will defer to auth state change to retry implicitly.');
                                    return [3 /*break*/, 6];
                                case 4:
                                    backoff_1 = 200 * Math.pow(2, attempt - 1);
                                    console.warn("[Auth] 401 on profile sync attempt ".concat(attempt, ". Retrying in ").concat(backoff_1, "ms"));
                                    return [4 /*yield*/, new Promise(function (res) { return setTimeout(res, backoff_1); })];
                                case 5:
                                    _d.sent();
                                    return [2 /*return*/, "continue"];
                                case 6: return [3 /*break*/, 8];
                                case 7:
                                    console.error('[Auth] Non-401 error updating profile:', status_1, (error_2 === null || error_2 === void 0 ? void 0 : error_2.message) || error_2);
                                    _d.label = 8;
                                case 8: return [2 /*return*/, "break"];
                                case 9: return [2 /*return*/];
                            }
                        });
                    };
                    attempt = 1;
                    _c.label = 11;
                case 11:
                    if (!(attempt <= maxAttempts)) return [3 /*break*/, 14];
                    return [5 /*yield**/, _loop_1(attempt)];
                case 12:
                    state_1 = _c.sent();
                    if (state_1 === "break")
                        return [3 /*break*/, 14];
                    _c.label = 13;
                case 13:
                    attempt++;
                    return [3 /*break*/, 11];
                case 14: return [3 /*break*/, 16];
                case 15:
                    error_1 = _c.sent();
                    friendlyMessage = getFriendlyErrorMessage(error_1, 'auth');
                    console.error('[Auth] Signup failed:', error_1);
                    throw new Error(friendlyMessage);
                case 16: return [2 /*return*/];
            }
        });
    }); };
    var login = function (email, password) { return __awaiter(void 0, void 0, void 0, function () {
        var cred, _a, error_3, friendlyMessage;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 6, , 7]);
                    return [4 /*yield*/, signInWithEmailAndPassword(auth, email, password)];
                case 1:
                    cred = _b.sent();
                    _b.label = 2;
                case 2:
                    _b.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, reload(cred.user)];
                case 3:
                    _b.sent();
                    return [3 /*break*/, 5];
                case 4:
                    _a = _b.sent();
                    return [3 /*break*/, 5];
                case 5: return [3 /*break*/, 7];
                case 6:
                    error_3 = _b.sent();
                    friendlyMessage = getFriendlyErrorMessage(error_3, 'auth');
                    console.error('[Auth] Login failed:', error_3);
                    throw new Error(friendlyMessage);
                case 7: return [2 /*return*/];
            }
        });
    }); };
    var loginWithGoogle = function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_4, friendlyMessage;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, signInWithPopup(auth, googleProvider)];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_4 = _a.sent();
                    friendlyMessage = getFriendlyErrorMessage(error_4, 'auth');
                    console.error('[Auth] Google login failed:', error_4);
                    throw new Error(friendlyMessage);
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var logout = function () { return __awaiter(void 0, void 0, void 0, function () {
        var token, error_5;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, getIdToken()];
                case 1:
                    token = _a.sent();
                    if (!token) return [3 /*break*/, 3];
                    return [4 /*yield*/, apiClient.post('/api/auth/logout', {}, {
                            headers: {
                                'Authorization': "Bearer ".concat(token)
                            }
                        })];
                case 2:
                    _a.sent();
                    _a.label = 3;
                case 3: return [3 /*break*/, 5];
                case 4:
                    error_5 = _a.sent();
                    console.error('Error notifying backend about logout:', error_5);
                    return [3 /*break*/, 5];
                case 5:
                    setUserProfile(null);
                    return [2 /*return*/, signOut(auth)];
            }
        });
    }); };
    var resetPassword = function (email) { return __awaiter(void 0, void 0, void 0, function () {
        var actionCodeSettings, friendlyMessage;
        return __generator(this, function (_a) {
            try {
                actionCodeSettings = {
                    url: (envVar('REACT_APP_ACTION_URL') || "".concat(window.location.origin, "/auth/action")),
                    handleCodeInApp: true,
                    dynamicLinkDomain: envVar('REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN'),
                };
                return [2 /*return*/, sendPasswordResetEmail(auth, email, actionCodeSettings)];
            }
            catch (error) {
                friendlyMessage = getFriendlyErrorMessage(error, 'auth');
                console.error('[Auth] Reset password failed:', error);
                throw new Error(friendlyMessage);
            }
            return [2 /*return*/];
        });
    }); };
    var getIdToken = function (forceRefresh) {
        if (forceRefresh === void 0) { forceRefresh = false; }
        return __awaiter(void 0, void 0, void 0, function () {
            var error_6;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!currentUser) return [3 /*break*/, 6];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 6]);
                        return [4 /*yield*/, currentUser.getIdToken(forceRefresh)];
                    case 2: return [2 /*return*/, _a.sent()];
                    case 3:
                        error_6 = _a.sent();
                        console.error('Error getting ID token:', error_6);
                        if (!forceRefresh) return [3 /*break*/, 5];
                        return [4 /*yield*/, signOut(auth)];
                    case 4:
                        _a.sent();
                        _a.label = 5;
                    case 5: return [2 /*return*/, null];
                    case 6: return [2 /*return*/, null];
                }
            });
        });
    };
    var fetchUserProfile = function (user) { return __awaiter(void 0, void 0, void 0, function () {
        var token, response, error_7;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, user.getIdToken()];
                case 1:
                    token = _b.sent();
                    return [4 /*yield*/, apiClient.get('/api/auth/profile', {
                            headers: {
                                'Authorization': "Bearer ".concat(token)
                            }
                        })];
                case 2:
                    response = _b.sent();
                    if (response.data.success) {
                        setUserProfile(response.data.profile);
                    }
                    else {
                        // Fallback to basic profile
                        setUserProfile({
                            uid: user.uid,
                            email: user.email,
                            name: user.displayName,
                            picture: user.photoURL
                        });
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_7 = _b.sent();
                    console.error('Error fetching user profile:', error_7);
                    // Check if it's an authentication error
                    if (((_a = error_7.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                        console.warn('Authentication failed when fetching profile, user may need to re-login');
                        // Don't sign out automatically here as it might be a temporary issue
                    }
                    // Always set basic profile from Firebase user as fallback
                    setUserProfile({
                        uid: user.uid,
                        email: user.email,
                        name: user.displayName,
                        picture: user.photoURL
                    });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var updateUserProfile = function (updates) { return __awaiter(void 0, void 0, void 0, function () {
        var token, response, error_8;
        var _a, _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    if (!currentUser) {
                        throw new Error('No user is currently signed in');
                    }
                    _e.label = 1;
                case 1:
                    _e.trys.push([1, 7, , 8]);
                    return [4 /*yield*/, currentUser.getIdToken()];
                case 2:
                    token = _e.sent();
                    return [4 /*yield*/, apiClient.put('/api/auth/profile', updates, {
                            headers: {
                                'Authorization': "Bearer ".concat(token),
                                'Content-Type': 'application/json'
                            }
                        })];
                case 3:
                    response = _e.sent();
                    if (!response.data.success) return [3 /*break*/, 5];
                    // Refresh user profile
                    return [4 /*yield*/, fetchUserProfile(currentUser)];
                case 4:
                    // Refresh user profile
                    _e.sent();
                    return [3 /*break*/, 6];
                case 5:
                    console.warn('Profile update response indicates failure:', response.data);
                    throw new Error(response.data.error || 'Profile update failed');
                case 6: return [3 /*break*/, 8];
                case 7:
                    error_8 = _e.sent();
                    console.error('Error updating user profile:', error_8);
                    // Provide more specific error messages
                    if (((_a = error_8.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                        throw new Error('Authentication failed. Please sign in again.');
                    }
                    else if (((_b = error_8.response) === null || _b === void 0 ? void 0 : _b.status) === 400) {
                        throw new Error(((_c = error_8.response.data) === null || _c === void 0 ? void 0 : _c.error) || 'Invalid profile data provided.');
                    }
                    else if (((_d = error_8.response) === null || _d === void 0 ? void 0 : _d.status) >= 500) {
                        throw new Error('Server error. Please try again later.');
                    }
                    else {
                        throw new Error(error_8.message || 'Failed to update profile. Please try again.');
                    }
                    return [3 /*break*/, 8];
                case 8: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        var unsubscribe = onAuthStateChanged(auth, function (user) { return __awaiter(void 0, void 0, void 0, function () {
            var token, error_9;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setCurrentUser(user);
                        if (!user) return [3 /*break*/, 7];
                        setEmailVerified(!!user.emailVerified);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 5, , 6]);
                        return [4 /*yield*/, user.getIdToken()];
                    case 2:
                        token = _a.sent();
                        return [4 /*yield*/, apiClient.post('/api/auth/verify', {}, {
                                headers: {
                                    'Authorization': "Bearer ".concat(token)
                                }
                            })];
                    case 3:
                        _a.sent();
                        // Fetch full user profile
                        return [4 /*yield*/, fetchUserProfile(user)];
                    case 4:
                        // Fetch full user profile
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        error_9 = _a.sent();
                        console.error('Error verifying token with backend:', error_9);
                        // Set basic profile from Firebase user
                        setUserProfile({
                            uid: user.uid,
                            email: user.email,
                            name: user.displayName,
                            picture: user.photoURL
                        });
                        return [3 /*break*/, 6];
                    case 6: return [3 /*break*/, 8];
                    case 7:
                        setUserProfile(null);
                        setEmailVerified(false);
                        _a.label = 8;
                    case 8:
                        setLoading(false);
                        return [2 /*return*/];
                }
            });
        }); });
        return unsubscribe;
    }, []);
    var resendVerificationEmail = function () { return __awaiter(void 0, void 0, void 0, function () {
        var actionCodeSettings;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!currentUser)
                        throw new Error('Not signed in');
                    actionCodeSettings = {
                        url: (envVar('REACT_APP_ACTION_URL') || "".concat(window.location.origin, "/auth/action")),
                        handleCodeInApp: true,
                        dynamicLinkDomain: envVar('REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN'),
                    };
                    return [4 /*yield*/, sendEmailVerification(currentUser, actionCodeSettings)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    // Setup axios interceptor to automatically add auth headers
    useEffect(function () {
        if (currentUser) {
            // Set up authentication interceptors for both apiClient and uploadApiClient
            setupAuthInterceptors(function () { return __awaiter(void 0, void 0, void 0, function () {
                var error_10;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            _a.trys.push([0, 2, , 3]);
                            return [4 /*yield*/, currentUser.getIdToken()];
                        case 1: return [2 /*return*/, _a.sent()];
                        case 2:
                            error_10 = _a.sent();
                            console.error('Error getting token:', error_10);
                            return [2 /*return*/, null];
                        case 3: return [2 /*return*/];
                    }
                });
            }); });
        }
        var responseInterceptor = apiClient.interceptors.response.use(function (response) { return response; }, function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var token, refreshError_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && currentUser)) return [3 /*break*/, 5];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 5]);
                        return [4 /*yield*/, currentUser.getIdToken(true)];
                    case 2:
                        token = _b.sent();
                        if (token) {
                            // Retry the original request with new token
                            error.config.headers.Authorization = "Bearer ".concat(token);
                            return [2 /*return*/, apiClient.request(error.config)];
                        }
                        return [3 /*break*/, 5];
                    case 3:
                        refreshError_1 = _b.sent();
                        console.error('Token refresh failed:', refreshError_1);
                        // Force logout if token refresh fails
                        return [4 /*yield*/, signOut(auth)];
                    case 4:
                        // Force logout if token refresh fails
                        _b.sent();
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/, Promise.reject(error)];
                }
            });
        }); });
        return function () {
            apiClient.interceptors.response.eject(responseInterceptor);
        };
    }, [currentUser]);
    var value = useMemo(function () { return ({
        currentUser: currentUser,
        userProfile: userProfile,
        loading: loading,
        signup: signup,
        login: login,
        loginWithGoogle: loginWithGoogle,
        logout: logout,
        resetPassword: resetPassword,
        updateUserProfile: updateUserProfile,
        getIdToken: getIdToken,
        isAuthenticated: !!currentUser,
        emailVerified: emailVerified,
        resendVerificationEmail: resendVerificationEmail,
    }); }, [
        currentUser,
        userProfile,
        loading,
        emailVerified
    ]);
    return (_jsx(AuthContext.Provider, __assign({ value: value }, { children: !loading && children })));
};
